import React, { useMemo, useState } from "react";
import styles from "./KPIAccordionTable.module.scss";
import GenericTable from "../../../../common/GenericTable/GenericTable";
import { isEmpty } from "lodash";
import { useTranslation } from "react-i18next";

interface KPIDataValue {
  kpiDataType?: string;
  kpiValue?: string | number;
  kpiCurrency?: string;
  groupName?: string;
  rowComponent?: KPIDataValue[][];
  children?: KPIDataValue[][];
}

interface KPIAccordionTableProps {
  data: {
    header: {
      row?: KPIDataValue[];
    };
    body: {
      rows?: KPIDataValue[][];
    };
  };
  affiliateCode?: string;
  kpiCode?: string;
  highlightColumns?: number[];
  groupedColumnClass?: boolean;
  showPills?: boolean;
  title?: string;
  subtitle?: string;
  groupedRowsSize?: number;
}

const KPIAccordionTable: React.FC<KPIAccordionTableProps> = ({
  data,
  affiliateCode,
  kpiCode,
  highlightColumns,
  groupedColumnClass,
  showPills = false,
  title,
  subtitle,
  groupedRowsSize,
}) => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState("all");
  const pills = useMemo(() => {
    if (!showPills || !data?.body?.rows) {
      return [];
    }
    const categories = new Set<string>();

    data.body.rows.forEach((row: KPIDataValue[]) => {
      if (row?.[0]?.groupName) {
        categories.add(row[0].groupName.toLowerCase());
      }
    });

    return Array.from(categories).map((cat) => ({
      label: cat.charAt(0).toUpperCase() + cat.slice(1).toLowerCase(),
      value: cat,
    }));
  }, [data, showPills]);

  const tableData = useMemo(() => {
    if (
      !data?.body?.rows ||
      !showPills ||
      selectedCategory.toLowerCase() === "all"
    ) {
      return data;
    }

    const filteredRows = data.body.rows.filter(
      (row) =>
        row?.[0]?.groupName?.toLowerCase() === selectedCategory.toLowerCase()
    );

    return {
      ...data,
      body: {
        ...data.body,
        rows: filteredRows,
      },
    };
  }, [data, showPills, selectedCategory]);

  return (
    <div className={styles.container}>
      {title || subtitle ? (
        <div className={styles.header}>
          {title ? <h2>{t(title)}</h2> : null}
          {subtitle ? <p>{t(subtitle)}</p> : null}
        </div>
      ) : null}

      {showPills && pills.length > 1 && (
        <div className={styles.pills_wrapper}>
          {pills.map((pill) => (
            <div
              key={pill.value}
              onClick={() => setSelectedCategory(pill.value)}
              className={`${styles.view_btn} ${
                selectedCategory === pill.value ? styles.selected_view : ""
              }`}
            >
              <span className={styles.label}>{pill.label}</span>
            </div>
          ))}
        </div>
      )}
      {isEmpty(tableData?.body?.rows) ? (
        <div className={styles.no_data}>{t("noDataAvailable")}</div>
      ) : (
        <GenericTable
          tableData={tableData}
          kpiCode={kpiCode}
          affiliateCode={affiliateCode}
          highlightColumns={highlightColumns}
          groupedColumnClass={groupedColumnClass}
          emptyValuePlaceholder=""
          groupedRowsSize={groupedRowsSize}
          isOnlyNegativeHighlight={true}
        />
      )}
    </div>
  );
};

export default KPIAccordionTable;
